// Publikus cikk. Ugyanaz az olvasó-tipográfia, mint belül — a szöveg ugyanolyan fontos egy
// ügyfélnek, mint egy kollégának. Ami hiányzik: szerkesztés, címkék, belső hozzászólások.
//
// A KIVEZETŐ utak 2026-08-05-ben kerültek ide: a lap alja korábban teljes zsákutca volt (se „fel a
// könyvhöz", se előző/következő), és hosszú cikknél fejezet-navigáció sem volt. A belső oldalon
// mindhárom megvolt — az ügyfél kapta a szűkebb felületet, holott ugyanúgy sorozatot olvas.
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { KnowledgeCrumbs } from '@/components/shared/knowledge-crumbs';
import { Skeleton } from '@/components/ui/skeleton';
import { ArticleFooterNav } from '@/features/knowledge/components/article-footer-nav';
import { Markdown } from '@/features/knowledge/components/markdown';
import { TocNav } from '@/features/knowledge/components/toc-nav';
import { extractHeadings } from '@/features/knowledge/lib/toc';
import { usePublicArticle, usePublicSpace } from '@/features/public/api';
import { PublicShell } from '@/features/public/components/public-shell';

/**
 * Ennyi fejezettől érdemes fejezet-navigációt adni. Kevesebbnél a lista maga hosszabb, mint amennyi
 * görgetést megspórol — a belső oldalon ez a döntés a jobb oldali sávban „ingyen" van, itt viszont a
 * szöveg FÖLÉ kerül (a publikus váz szűk, nincs oldalsáv), ezért kell a küszöb.
 */
const TOC_FROM = 3;

export const Route = createFileRoute('/sugo/$spaceSlug/$categorySlug/$articleSlug')({
  component: PublicArticlePage,
});

const dateFormat = new Intl.DateTimeFormat('hu-HU', { dateStyle: 'long' });

function PublicArticlePage(): JSX.Element {
  const { t } = useTranslation();
  const params = Route.useParams();
  const query = usePublicArticle(params);
  const page = query.data?.status === 200 ? query.data.body : null;
  const headings = useMemo(() => (page ? extractHeadings(page.article.body) : []), [page]);
  // A szomszéd-cikkekhez a szekció tartalma kell. Szerver-oldalon közönségenként cache-elt (60 s),
  // tehát ez nem egy újabb busz-bejárás — és a `/sugo/<szekció>` oldal ugyanezt a kulcsot használja.
  const spaceQuery = usePublicSpace(page ? page.space.slug : null);
  const spacePage = spaceQuery.data?.status === 200 ? spaceQuery.data.body : null;

  if (query.isLoading) {
    return (
      <PublicShell>
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="mt-8 h-64 w-full" />
      </PublicShell>
    );
  }

  if (!page) {
    return (
      <PublicShell>
        <p className="text-muted-foreground">{t('public.notFound')}</p>
        <Link to="/sugo" className="text-primary mt-4 inline-block underline underline-offset-2">
          {t('public.backToHelp')}
        </Link>
      </PublicShell>
    );
  }

  return (
    <PublicShell
      internalTarget={{
        spaceSlug: page.canonical.spaceSlug,
        categorySlug: page.canonical.categorySlug,
        articleSlug: page.canonical.articleSlug,
      }}
    >
      {/* Ugyanaz a morzsa-komponens, mint belül — a könyv szintje eddig kimaradt, így a publikus
          olvasó nem tudott a szomszédos cikkekhez lépni. */}
      <KnowledgeCrumbs
        variant="public"
        path={{ space: page.space, category: page.category }}
        article={{ title: page.article.title, slug: null }}
      />

      <article className="mt-6">
        <h1 className="text-read-2xl max-w-[68ch] font-semibold tracking-tight">
          {page.article.title}
        </h1>
        {page.article.summary && (
          <p className="text-muted-foreground text-read-base mt-3 max-w-[68ch] font-serif">
            {page.article.summary}
          </p>
        )}
        <p className="text-subtle border-border mt-5 max-w-[68ch] border-b pb-5 text-sm">
          {t('knowledge.updated', { date: dateFormat.format(new Date(page.article.updatedAt)) })}
        </p>

        {headings.length >= TOC_FROM && (
          <div className="border-border mt-7 max-w-[68ch] rounded-xl border p-4">
            <TocNav items={headings} />
          </div>
        )}

        <div className="mt-8">
          <Markdown source={page.article.body} headings={headings} />
        </div>

        <ArticleFooterNav
          variant="public"
          space={spacePage}
          categorySlug={page.canonical.categorySlug}
          articleSlug={page.article.slug}
        />
      </article>
    </PublicShell>
  );
}
